export default el => {
  const track = el.querySelector('[data-track]')
  const prevArrow = el.querySelector('[data-prev]')
  const nextArrow = el.querySelector('[data-next]')

  if (!track) return

  const arrows = [prevArrow, nextArrow].filter(Boolean)
  const focusableLinkSelector = 'a.redesign-shop-by-category__link'
  const items = Array.from(track.querySelectorAll('.redesign-shop-by-category__item'))

  if (items.length <= 1) {
    arrows.forEach(arrow => {
      arrow.hidden = true
    })
    return
  }

  // Native horizontal scroll: it physically cannot scroll past the content, so
  // the last item always lands flush against the right edge — no trailing gap.
  // (Slick with variableWidth could never do this: it only lands on whole-slide
  //  offsets, and the flush-right position falls between two slides.)
  const SCROLL_EPSILON = 2

  const getMaxScroll = () => track.scrollWidth - track.clientWidth

  // Advance by almost a full viewport, keeping one item peeking for context.
  const getStep = () => {
    const itemWidth = items[0] ? items[0].getBoundingClientRect().width : 0
    return Math.max(itemWidth, track.clientWidth - itemWidth)
  }

  const setArrowState = (arrow, disabled) => {
    if (!arrow) return
    arrow.classList.toggle('slick-disabled', disabled)
    arrow.setAttribute('aria-disabled', disabled ? 'true' : 'false')
  }

  const updateArrows = () => {
    const maxScroll = getMaxScroll()

    // Everything fits — nothing to scroll, hide the controls.
    if (maxScroll <= SCROLL_EPSILON) {
      arrows.forEach(arrow => {
        arrow.hidden = true
      })
      return
    }

    arrows.forEach(arrow => {
      arrow.hidden = false
    })

    setArrowState(prevArrow, track.scrollLeft <= SCROLL_EPSILON)
    setArrowState(nextArrow, track.scrollLeft >= maxScroll - SCROLL_EPSILON)
  }

  const syncVisibleSlideFocus = () => {
    const viewportRect = track.getBoundingClientRect()

    items.forEach(item => {
      const link = item.querySelector(focusableLinkSelector)
      const itemRect = item.getBoundingClientRect()
      const isVisible =
        itemRect.right > viewportRect.left + 1 &&
        itemRect.left < viewportRect.right - 1

      item.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

      if (link) {
        link.tabIndex = isVisible ? 0 : -1
      }
    })
  }

  const scrollByStep = direction => {
    track.scrollBy({ left: direction * getStep(), behavior: 'smooth' })
  }

  const handleKeydown = event => {
    if (event.defaultPrevented) return

    const target = event.target
    const isEditable =
      target &&
      target.matches &&
      target.matches('input, textarea, select, [contenteditable="true"]')

    if (isEditable) return

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      scrollByStep(-1)
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      scrollByStep(1)
    }
  }

  // Mouse drag-to-scroll. Native overflow scrolling already handles touch/
  // trackpad/wheel, but a plain mouse can't drag it — wire that up via pointer
  // events (touch is left to the native scroller).
  const DRAG_THRESHOLD = 5
  let isPointerDown = false
  let dragMoved = false
  let dragStartX = 0
  let dragStartScroll = 0

  const onPointerDown = event => {
    if (event.pointerType === 'touch' || event.button !== 0) return
    isPointerDown = true
    dragMoved = false
    dragStartX = event.clientX
    dragStartScroll = track.scrollLeft
  }

  const onPointerMove = event => {
    if (!isPointerDown) return
    const dx = event.clientX - dragStartX

    if (!dragMoved && Math.abs(dx) > DRAG_THRESHOLD) {
      dragMoved = true
      track.classList.add('is-dragging')
      if (track.setPointerCapture) track.setPointerCapture(event.pointerId)
    }

    if (dragMoved) {
      event.preventDefault()
      track.scrollLeft = dragStartScroll - dx
    }
  }

  const endPointerDrag = () => {
    isPointerDown = false
    track.classList.remove('is-dragging')
  }

  // Swallow the click that fires after a drag so links don't navigate.
  const onClickCapture = event => {
    if (!dragMoved) return
    event.preventDefault()
    event.stopPropagation()
    dragMoved = false
  }

  // Block native image/link ghost-dragging while pointer-dragging the track.
  const onDragStart = event => event.preventDefault()

  let rafId = null
  const onScroll = () => {
    if (rafId) return
    rafId = window.requestAnimationFrame(() => {
      rafId = null
      updateArrows()
      syncVisibleSlideFocus()
    })
  }

  if (prevArrow) prevArrow.addEventListener('click', () => scrollByStep(-1))
  if (nextArrow) nextArrow.addEventListener('click', () => scrollByStep(1))
  el.addEventListener('keydown', handleKeydown)
  track.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)

  track.addEventListener('pointerdown', onPointerDown)
  track.addEventListener('pointermove', onPointerMove)
  track.addEventListener('pointerup', endPointerDrag)
  track.addEventListener('pointercancel', endPointerDrag)
  track.addEventListener('click', onClickCapture, true)
  track.addEventListener('dragstart', onDragStart)

  updateArrows()
  syncVisibleSlideFocus()
}
